import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  CircularProgress,
} from "@mui/material";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import VuiAutocomplete from "components/AutoComplete";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

import { FaPlus, FaTrash, FaExclamationCircle } from "react-icons/fa";
import { TEMPLATE_FIELD_TYPES } from "variables";
import OverlayLoader from "components/OverlayLoader";
import { MAX_SECTIONS_PER_TEMPLATE } from "variables";
import { v4 as uuidv4 } from "uuid";

const supportsOptions = (fieldType) =>
  ["checkbox_list", "bullet_point_list", "radio_buttons"].includes(fieldType);

const emptySection = {
  sectionId: null,
  title: "",
  subheading: "",
  fieldType: "",
  options: [],
};

const CreateTemplateDialog = ({
  open,
  onClose,
  onSave,
  loading = false,
  fetchLoading = false,
  error = "",
  template = null,
}) => {
  const [form, setForm] = useState({
    templateName: "",
    heading: "",
    description: "",
  });

  const [sections, setSections] = useState([{ ...emptySection }]);
  const [errors, setErrors] = useState({});
  const [warning, setWarning] = useState("");
  const { dark, light, primary } = colors;
  const errorRef = useRef();
  const fieldRefs = useRef({});

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [error]);

  useEffect(() => {
    if (!open) return;

    if (template) {
      setForm({
        templateName: template.name || "",
        heading: template.workspaceHeading || "",
        description: template.description || "",
      });

      setSections(
        template.sections?.map((section) => ({
          sectionId: section.sectionId,
          title: section.title || "",
          subheading: section.subheading || "",
          fieldType: section.fieldType || "",
          options: section.fieldConfig?.options || [],
        })) || [{ ...emptySection }]
      );
    } else {
      setForm({
        templateName: "",
        heading: "",
        description: "",
      });

      setWarning("");
      setSections([{ ...emptySection }]);
    }

    setErrors({});
  }, [open, template]);

  const setFieldRef = (key) => (element) => {
    if (element) {
      fieldRefs.current[key] = element;
    }
  };

  const scrollToFirstError = (newErrors) => {
    const firstErrorKey = Object.keys(newErrors)[0];

    if (!firstErrorKey) return;

    setTimeout(() => {
      fieldRefs.current[firstErrorKey]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 100);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: null,
    }));
  };

  const updateSection = (index, field, value) => {
    setSections((prev) =>
      prev.map((section, i) =>
        i === index
          ? {
              ...section,
              [field]: value,
            }
          : section
      )
    );

    setErrors((prev) => {
      const updatedErrors = { ...prev };

      delete updatedErrors[`sections.${index}.${field}`];

      if (field === "options") {
        delete updatedErrors[`sections.${index}.options`];

        Object.keys(updatedErrors).forEach((key) => {
          if (key.startsWith(`sections.${index}.options.`)) {
            delete updatedErrors[key];
          }
        });
      }

      return updatedErrors;
    });
  };

  const addSection = () => {
    if (sections.length >= MAX_SECTIONS_PER_TEMPLATE) {
      setWarning("A template can contain a maximum of 20 sections.");
      return;
    }

    setSections((prev) => [...prev, { ...emptySection }]);
  };

  const removeSection = (index) => {
    setSections((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const newErrors = {};

    if (!form.templateName.trim()) {
      newErrors.templateName = "Template name is required";
    }

    if (!form.heading.trim()) {
      newErrors.heading = "Workspace heading is required";
    }

    if (!sections.length || sections.length > MAX_SECTIONS_PER_TEMPLATE) {
      newErrors.sections = `Add between 1 and ${MAX_SECTIONS_PER_TEMPLATE} sections`;
    }

    const titleMap = new Map();

    sections.forEach((section, index) => {
      const title = section.title.trim();
      const subheading = section.subheading.trim();

      if (!title) {
        newErrors[`sections.${index}.title`] = "Section title is required";
      } else if (title.length < 4) {
        newErrors[`sections.${index}.title`] = "Section title must be more than 3 characters";
      } else {
        const normalizedTitle = title.toLowerCase();

        if (titleMap.has(normalizedTitle)) {
          newErrors[`sections.${index}.title`] = "Section title must be unique";

          const firstIndex = titleMap.get(normalizedTitle);
          newErrors[`sections.${firstIndex}.title`] = "Section title must be unique";
        } else {
          titleMap.set(normalizedTitle, index);
        }
      }

      if (subheading && subheading.length < 4) {
        newErrors[`sections.${index}.subheading`] =
          "Section subheading must be more than 3 characters";
      }

      if (!section.fieldType) {
        newErrors[`sections.${index}.fieldType`] = "Field type is required";
      }

      if (supportsOptions(section.fieldType)) {
        const options = section.options || [];
        const validOptions = options.filter((option) => option?.label?.trim());

        if (section.fieldType === "radio_buttons" && validOptions.length < 2) {
          newErrors[`sections.${index}.options`] =
            "Radio button field must have at least 2 options";
        }

        const optionMap = new Map();

        options.forEach((option, optionIndex) => {
          const label = option?.label?.trim() || "";

          if (!label) {
            newErrors[`sections.${index}.options.${optionIndex}`] = "Option name is required";
            return;
          }

          const normalizedLabel = label.toLowerCase();

          if (optionMap.has(normalizedLabel)) {
            newErrors[`sections.${index}.options.${optionIndex}`] = "Option name must be unique";

            const firstOptionIndex = optionMap.get(normalizedLabel);
            newErrors[`sections.${index}.options.${firstOptionIndex}`] =
              "Option name must be unique";
          } else {
            optionMap.set(normalizedLabel, optionIndex);
          }
        });
      }
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      scrollToFirstError(newErrors);
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (loading || fetchLoading) return;
    if (!validate()) return;

    const payload = {
      templateName: form.templateName.trim(),
      heading: form.heading.trim(),
      description: form.description.trim(),

      sections: sections.map((section, index) => ({
        sectionId: section.sectionId,
        order: index + 1,
        title: section.title.trim(),
        subheading: section.subheading.trim(),
        fieldType: section.fieldType,
        options: supportsOptions(section.fieldType)
          ? (section.options || []).map((option) => ({
              key: option.key || `option_${uuidv4()}`,
              label: option.label.trim(),
            }))
          : [],
      })),
    };

    onSave?.(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (loading) return;
        if (reason === "backdropClick") return;
        onClose();
      }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <VuiTypography variant="h5">
          {template ? "Edit" : "Create"} Operations Template
        </VuiTypography>
      </DialogTitle>

      <DialogContent>
        <OverlayLoader open={fetchLoading} message="Loading template..." />

        <OverlayLoader
          open={loading}
          message={template ? "Updating template..." : "Creating new template..."}
        />

        <VuiBox display="flex" flexDirection="column" gap={1} mt={0}>
          {/* TEMPLATE NAME */}
          <VuiBox ref={setFieldRef("templateName")}>
            <VuiTypography variant="caption" ml={1}>
              Template Name*
            </VuiTypography>

            <VuiInput
              gradient
              fullWidth
              value={form.templateName}
              inputProps={{ maxLength: 250 }}
              onChange={(e) => handleChange("templateName", e.target.value)}
              error={!!errors.templateName}
            />

            {errors.templateName && (
              <VuiTypography variant="caption" color="error">
                {errors.templateName}
              </VuiTypography>
            )}
          </VuiBox>

          {/* HEADING */}
          <VuiBox ref={setFieldRef("heading")}>
            <VuiTypography variant="caption" ml={1}>
              Workspace Heading*
            </VuiTypography>

            <VuiInput
              gradient
              fullWidth
              value={form.heading}
              onChange={(e) => handleChange("heading", e.target.value)}
              error={!!errors.heading}
              inputProps={{ maxLength: 250 }}
            />

            {errors.heading && (
              <VuiTypography variant="caption" color="error">
                {errors.heading}
              </VuiTypography>
            )}
          </VuiBox>

          {/* DESCRIPTION */}
          <VuiBox>
            <VuiTypography variant="caption" ml={1}>
              Description
            </VuiTypography>

            <VuiInput
              gradient
              fullWidth
              multiline
              rows={3}
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              inputProps={{
                maxLength: 150,
              }}
            />
          </VuiBox>

          <Divider sx={{ my: 1 }} />

          {/* SECTION HEADER */}
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" gap={2}>
            <VuiBox>
              <VuiTypography variant="button" fontWeight="bold">
                Template Sections
              </VuiTypography>

              <VuiTypography variant="caption" display={{ xs: "none", sm: "block" }}>
                Configure the operational structure of this template.
              </VuiTypography>
            </VuiBox>
          </VuiBox>

          {/* SECTIONS */}
          {sections.map((section, index) => (
            <VuiBox
              key={index}
              p={2}
              mt={1}
              borderRadius="lg"
              sx={{
                border: `1px solid ${rgba(dark.main, 0.25)}`,
                background: "rgba(255,255,255,0.01)",
              }}
            >
              <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <VuiTypography variant="button" fontWeight="bold">
                  {section.title || `Section ${index + 1}`}
                </VuiTypography>

                {sections.length > 1 && (
                  <VuiButton
                    color="error"
                    variant="outlined"
                    size="small"
                    onClick={() => removeSection(index)}
                    sx={{
                      border: 0,
                    }}
                  >
                    <FaTrash
                      size={10}
                      style={{
                        marginRight: 6,
                      }}
                    />
                    Remove
                  </VuiButton>
                )}
              </VuiBox>

              <VuiBox display="flex" flexDirection="column" gap={0.5}>
                {/* SECTION TITLE */}
                <VuiBox ref={setFieldRef(`sections.${index}.title`)}>
                  <VuiTypography variant="caption" ml={1}>
                    Section Title*
                  </VuiTypography>

                  <VuiInput
                    gradient
                    fullWidth
                    value={section.title}
                    inputProps={{ maxLength: 300 }}
                    onChange={(e) => updateSection(index, "title", e.target.value)}
                    error={!!errors[`sections.${index}.title`]}
                  />

                  {errors[`sections.${index}.title`] && (
                    <VuiTypography variant="caption" color="error">
                      {errors[`sections.${index}.title`]}
                    </VuiTypography>
                  )}
                </VuiBox>

                {/* SECTION SUBTITLE */}
                {/* SECTION SUBTITLE */}
                <VuiBox ref={setFieldRef(`sections.${index}.subheading`)}>
                  <VuiTypography variant="caption" ml={1}>
                    Section Subheading
                  </VuiTypography>

                  <VuiInput
                    gradient
                    fullWidth
                    value={section.subheading}
                    inputProps={{ maxLength: 400 }}
                    onChange={(e) => updateSection(index, "subheading", e.target.value)}
                    error={!!errors[`sections.${index}.subheading`]}
                  />

                  {errors[`sections.${index}.subheading`] && (
                    <VuiTypography variant="caption" color="error">
                      {errors[`sections.${index}.subheading`]}
                    </VuiTypography>
                  )}
                </VuiBox>

                {/* FIELD TYPE */}
                <VuiBox ref={setFieldRef(`sections.${index}.fieldType`)}>
                  <VuiTypography variant="caption" ml={1}>
                    Field Type*
                  </VuiTypography>

                  <VuiAutocomplete
                    options={TEMPLATE_FIELD_TYPES}
                    value={
                      TEMPLATE_FIELD_TYPES.find((type) => type.value === section.fieldType) || null
                    }
                    getOptionLabel={(option) => option?.label || ""}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    placeholder="Select field type"
                    gradient
                    fullWidth
                    onChange={(_, selected) =>
                      updateSection(index, "fieldType", selected?.value || "")
                    }
                  />

                  {errors[`sections.${index}.fieldType`] && (
                    <VuiTypography variant="caption" color="error">
                      {errors[`sections.${index}.fieldType`]}
                    </VuiTypography>
                  )}
                </VuiBox>

                {supportsOptions(section.fieldType) && (
                  <VuiBox ref={setFieldRef(`sections.${index}.options`)}>
                    <VuiTypography variant="caption" ml={1}>
                      {section.fieldType === "radio_buttons"
                        ? "Radio Button Options*"
                        : "Predefined Items (optional)"}
                    </VuiTypography>
                    {section.fieldType !== "radio_buttons" && (
                      <VuiTypography variant="caption" display="block" ml={1}>
                        Add the items users should start with in this section.
                      </VuiTypography>
                    )}

                    <VuiBox display="flex" flexDirection="column" gap={1} mt={1}>
                      {(section.options || []).map((option, optionIndex) => (
                        <VuiBox
                          key={optionIndex}
                          ref={setFieldRef(`sections.${index}.options.${optionIndex}`)}
                        >
                          <VuiBox display="flex" gap={1}>
                            <VuiInput
                              gradient
                              fullWidth
                              placeholder={`${section.fieldType === "radio_buttons" ? "Option" : "Item"} ${optionIndex + 1}`}
                              value={option.label || ""}
                              inputProps={{ maxLength: 500 }}
                              error={!!errors[`sections.${index}.options.${optionIndex}`]}
                              onChange={(e) => {
                                const optionValue = e.target.value;
                                const updatedOptions = [...(section.options || [])];

                                updatedOptions[optionIndex] = {
                                  key: option.key || `option_${uuidv4()}`,
                                  label: optionValue,
                                };

                                updateSection(index, "options", updatedOptions);
                              }}
                            />

                            <VuiButton
                              color="error"
                              variant="outlined"
                              onClick={() => {
                                const updatedOptions = (section.options || []).filter(
                                  (_, i) => i !== optionIndex
                                );

                                updateSection(index, "options", updatedOptions);
                              }}
                            >
                              <FaTrash size={10} />
                            </VuiButton>
                          </VuiBox>

                          {errors[`sections.${index}.options.${optionIndex}`] && (
                            <VuiTypography variant="caption" color="error">
                              {errors[`sections.${index}.options.${optionIndex}`]}
                            </VuiTypography>
                          )}
                        </VuiBox>
                      ))}

                      {errors[`sections.${index}.options`] && (
                        <VuiTypography variant="caption" color="error">
                          {errors[`sections.${index}.options`]}
                        </VuiTypography>
                      )}

                      <VuiButton
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() =>
                          updateSection(index, "options", [
                            ...(section.options || []),
                            {
                              key: `option_${uuidv4()}`,
                              label: "",
                            },
                          ])
                        }
                      >
                        <FaPlus size={10} style={{ marginRight: 6 }} />
                        {section.fieldType === "radio_buttons" ? "Add Option" : "Add Item"}
                      </VuiButton>
                    </VuiBox>
                  </VuiBox>
                )}
              </VuiBox>
            </VuiBox>
          ))}

          {errors.sections && (
            <VuiTypography variant="caption" color="error">
              {errors.sections}
            </VuiTypography>
          )}
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" mt={2}>
            <VuiTypography variant="caption">
              {sections.length} / {MAX_SECTIONS_PER_TEMPLATE} sections
            </VuiTypography>

            <VuiButton color="primary" size="small" onClick={addSection}>
              <FaPlus size={10} style={{ marginRight: 6 }} />
              Add Section
            </VuiButton>
          </VuiBox>

          {/* ERROR */}
          {error && (
            <VuiBox
              ref={errorRef}
              display="flex"
              alignItems="center"
              gap={1}
              p={1.2}
              mt={2}
              borderRadius="md"
              sx={{
                border: "1px solid rgba(239,68,68,0.4)",
                background: "rgba(239,68,68,0.08)",
              }}
            >
              <FaExclamationCircle size={14} color="#ef4444" />

              <VuiTypography variant="caption" color="error">
                {error}
              </VuiTypography>
            </VuiBox>
          )}

          {/* WARNING */}
          {warning && (
            <VuiBox
              display="flex"
              alignItems="center"
              gap={1}
              p={1.2}
              mt={2}
              borderRadius="md"
              sx={{
                border: `1px solid ${rgba(dark.main, 0.25)}`,
                background: light.secondary,
              }}
            >
              <FaExclamationCircle size={14} color={primary.main} />

              <VuiTypography variant="caption" color="primary">
                {warning}
              </VuiTypography>
            </VuiBox>
          )}
        </VuiBox>
      </DialogContent>

      <DialogActions>
        <VuiButton variant="outlined" color="primary" onClick={onClose} disabled={loading}>
          Cancel
        </VuiButton>

        <VuiButton
          color="success"
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || fetchLoading}
        >
          {/* {loading && <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />} */}

          {loading ? "Saving..." : template ? "Update Template" : "Save Template"}
        </VuiButton>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTemplateDialog;
